// REACT IMPORTS
import React, { useEffect, useRef, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { patchAPICall } from "../../../config/apiCalls";

// MUI COMPONENTS
import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
} from "@mui/material";

/**
 * Confirms retiring a product (setting it Inactive) or reactivating it.
 * Products are never deleted: retired ones keep their history and can come
 * back.
 * @param {Object|null} props.product - open when set
 * @param {Function} props.onClose
 * @param {Function} props.onSaved - (product, changed) after the change
 */
const RetireProductDialog = ({ product, onClose, onSaved }) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// STATE
	const [isSaving, setIsSaving] = useState(false);
	const [errorText, setErrorText] = useState("");

	useEffect(() => {
		setErrorText("");
	}, [product]);

	// Keep the last product while the dialog fades out, so the title doesn't flip
	const lastProduct = useRef(product);
	if (product) lastProduct.current = product;
	const retiring = lastProduct.current ? lastProduct.current.active : true;

	const handleConfirm = async () => {
		setIsSaving(true);
		setErrorText("");
		try {
			const res = await patchAPICall(
				instance,
				accounts[0],
				`/api/product-library/products/${product.id}`,
				{
					changes: { active: !product.active },
					expectedModified: product.modified,
				},
			);
			onSaved(res.data.product, res.data.changed || []);
		} catch (err) {
			console.error(err);
			setErrorText(
				err?.response?.data?.error || "Couldn't update this product.",
			);
		}
		setIsSaving(false);
	};

	return (
		<Dialog
			open={Boolean(product)}
			fullWidth
			maxWidth="sm"
			onClose={isSaving ? undefined : onClose}
		>
			<DialogTitle id="editProductDialogTitle">
				{retiring ? "Retire Product" : "Reactivate Product"}
			</DialogTitle>
			<DialogContent id="editProductDialogContent">
				{errorText && <Alert severity="error">{errorText}</Alert>}
				<div className="retireProductText">
					“{lastProduct.current?.text}”
				</div>
				<div>
					{retiring
						? "This sets the product to Inactive in HubSpot. It drops out of the Line Item Editor's library and HubSpot search, and keeps its history. Line items already on deals don't change. You can reactivate it from the Inactive tab."
						: "This sets the product back to Active in HubSpot, so it shows in the Line Item Editor's library again."}
				</div>
				<div className="dialogButtons">
					<Button
						onClick={onClose}
						variant="outlined"
						color="error"
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button
						onClick={handleConfirm}
						variant="contained"
						color={retiring ? "error" : "primary"}
						disableElevation
						disabled={isSaving}
						startIcon={
							isSaving ? (
								<CircularProgress size={16} color="inherit" />
							) : null
						}
					>
						{retiring ? "Retire" : "Reactivate"}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default RetireProductDialog;
